import {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
  CSSProperties,
  ImgHTMLAttributes,
  memo,
  SyntheticEvent,
} from 'react';

export interface OptimizedImageProps
  extends Omit<
    ImgHTMLAttributes<HTMLImageElement>,
    'src' | 'alt' | 'width' | 'height' | 'loading' | 'onLoad' | 'onError'
  > {
  src: string;
  alt: string;
  width?: string | number;
  height?: string | number;
  fill?: boolean;
  priority?: boolean;
  rootMargin?: string;
  sizes?: string;
  widths?: number[];
  objectFit?: CSSProperties['objectFit'];
  objectPosition?: CSSProperties['objectPosition'];
  quality?: number;
  loading?: 'lazy' | 'eager';
  onLoad?: (e: SyntheticEvent<HTMLImageElement>) => void;
  onError?: (e: SyntheticEvent<HTMLImageElement>) => void;
}

function buildSrcSet(
  src: string,
  widths: number[] | undefined
): string | undefined {
  if (!widths?.length) return undefined;
  if (src.startsWith('data:') || src.startsWith('blob:')) return undefined;
  return widths.map((w) => `${src}?w=${w} ${w}w`).join(', ');
}

const DEFAULT_DEVICE_WIDTHS = [640, 750, 828, 1080, 1200, 1920, 2048, 3840];

function useIntersectionObserver(
  ref: React.RefObject<HTMLElement>,
  options: { rootMargin?: string; threshold?: number | number[] } = {}
) {
  const [isVisible, setIsVisible] = useState(false);
  const { rootMargin = '200px', threshold = 0 } = options;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    if (typeof IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(el);
        }
      },
      { rootMargin, threshold }
    );

    observer.observe(el);
    return () => {
      observer.disconnect();
    };
  }, [ref, rootMargin, threshold]);

  return isVisible;
}

const OptimizedImageComponent = memo(
  ({
    src,
    alt,
    width,
    height,
    fill = false,
    priority = false,
    loading: loadingProp,
    rootMargin = '200px',
    sizes: sizesProp,
    widths,
    onLoad,
    onError,
    style,
    className,
    objectFit: objectFitProp,
    objectPosition: objectPositionProp,
    quality,
    ...rest
  }: OptimizedImageProps) => {
    const imgRef = useRef<HTMLImageElement>(null);

    const [status, setStatus] = useState<
      'idle' | 'loading' | 'loaded' | 'error'
    >('idle');
    const [shouldLoad, setShouldLoad] = useState(priority);

    const isVisible = useIntersectionObserver(imgRef, { rootMargin });

    useEffect(() => {
      if (priority || isVisible) {
        setShouldLoad(true);
      }
    }, [priority, isVisible]);

    useEffect(() => {
      if (shouldLoad && status === 'idle') {
        setStatus('loading');
      }
    }, [shouldLoad, status]);

    const handleLoad = useCallback(
      (e: SyntheticEvent<HTMLImageElement>) => {
        setStatus('loaded');
        onLoad?.(e);
      },
      [onLoad]
    );

    const handleError = useCallback(
      (e: SyntheticEvent<HTMLImageElement>) => {
        setStatus('error');
        onError?.(e);
      },
      [onError]
    );

    const effectiveSizes = useMemo(
      () => sizesProp ?? (fill ? '100vw' : width ? `${width}px` : '100%'),
      [fill, sizesProp, width]
    );

    const effectiveSrcSet = useMemo(
      () => buildSrcSet(src, widths ?? DEFAULT_DEVICE_WIDTHS),
      [src, widths]
    );

    const loadingAttr = loadingProp ?? (priority ? 'eager' : 'lazy');
    const objectFit = objectFitProp ?? (fill ? 'cover' : undefined);
    const objectPosition = objectPositionProp;

    const imgStyle = useMemo<CSSProperties>(
      () =>
        fill
          ? {
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit,
              objectPosition,
              transition: 'opacity 0.4s ease',
              opacity: status === 'loaded' ? 1 : 0,
              ...style,
            }
          : {
              display: 'block',
              width: width ? `${width}px` : '100%',
              height: height ? `${height}px` : 'auto',
              objectFit,
              objectPosition,
              transition: 'opacity 0.4s ease',
              opacity: status === 'loaded' ? 1 : 0,
              ...style,
            },
      [fill, height, objectFit, objectPosition, status, style, width]
    );

    return (
      <img
        ref={imgRef}
        src={shouldLoad ? src : undefined}
        srcSet={shouldLoad ? effectiveSrcSet : undefined}
        sizes={effectiveSizes}
        alt={alt}
        width={!fill ? width : undefined}
        height={!fill ? height : undefined}
        loading={loadingAttr as any}
        decoding="async"
        onLoad={handleLoad}
        onError={handleError}
        style={imgStyle}
        className={className}
        {...rest}
      />
    );
  },
  (prev, next) => {
    return (
      prev.src === next.src &&
      prev.alt === next.alt &&
      prev.width === next.width &&
      prev.height === next.height &&
      prev.priority === next.priority
    );
  }
);

OptimizedImageComponent.displayName = 'OptimizedImage';

export default OptimizedImageComponent;
export { useIntersectionObserver, buildSrcSet };
